import { useEffect, useRef, useState } from 'react';
import { IconButton, Tooltip, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import { useEntity } from '@backstage/plugin-catalog-react';
import { InfoCard } from '@backstage/core-components';
import { MachineIdDisplay } from '../fields/MachineIdDisplay/MachineIdDisplay';
import { groupLevelLabel } from '../../utils/groupLevelLabel';

const useStyles = makeStyles(theme => ({
  copy: {
    flexShrink: 0,
    // `@backstage/core-components`'s CopyTextButton always renders a solid
    // "primary" ButtonIcon with no style escape hatch. This mirrors the
    // transparent, text-sized copy icon TechDocs' mkdocs-material theme uses
    // inline next to code, rather than a standalone filled action button.
    // Rendered as MachineIdDisplay's endAdornment so it shares that row's
    // `alignItems: center` and lands centered on the ID row itself.
    padding: theme.spacing(0.5),
    marginLeft: 'auto',
    color: theme.palette.text.secondary,
  },
  copyIcon: {
    fontSize: '1.2rem',
  },
  hint: {
    display: 'block',
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
}));

const COPIED_TOOLTIP_DURATION_MS = 1500;

interface InlineCopyButtonProps {
  text: string;
  ariaLabel: string;
  className: string;
  iconClassName: string;
}

function InlineCopyButton({
  text,
  ariaLabel,
  className,
  iconClassName,
}: InlineCopyButtonProps) {
  // Holds the text of the last *successful* copy rather than a boolean, so the
  // confirmation is scoped to the exact value that was copied. `groupPage` is a
  // static element, so navigating from one group to another updates
  // `useEntity()` — and therefore this prop — without remounting the button
  // (the same reason TeamActions in packages/app's EntityPage keys by
  // `metadata.name`). Comparing against the current `text` drops the
  // confirmation in the very render that changes it, leaving none of the stale
  // frames a reset-in-an-effect would.
  const [copiedText, setCopiedText] = useState<string>();
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const copied = copiedText === text;

  useEffect(() => () => clearTimeout(timeoutRef.current), []);

  const copy = async () => {
    try {
      // Throws synchronously when `navigator.clipboard` is absent (any
      // non-secure context) and rejects when the permission is denied, so
      // both failure modes land here.
      await navigator.clipboard.writeText(text);
    } catch {
      // Leave the tooltip on "Copy to clipboard" — telling the user the copy
      // succeeded when it did not is worse than saying nothing. A failure
      // following an earlier success has to clear that success and its pending
      // reset too, or the tooltip would keep claiming the latest write landed.
      clearTimeout(timeoutRef.current);
      setCopiedText(undefined);
      return;
    }
    setCopiedText(text);
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(
      () => setCopiedText(undefined),
      COPIED_TOOLTIP_DURATION_MS,
    );
  };

  return (
    <Tooltip title={copied ? 'Text copied to clipboard' : 'Copy to clipboard'}>
      <IconButton
        size="small"
        className={className}
        onClick={() => {
          void copy();
        }}
        aria-label={ariaLabel}
      >
        <FileCopyIcon className={iconClassName} />
      </IconButton>
    </Tooltip>
  );
}

/**
 * Surfaces the group's canonical Machine ID on the read-only Group entity page.
 *
 * The Machine ID *is* `metadata.name` — computed by `computeMachineId()` and
 * written as the entity name by the team-management backend — so every group it
 * writes (teams and the function / sub-function / XP levels above them) already
 * carries it and no backend or schema change is needed. Until now it was only
 * ever rendered inside the create/edit forms, while downstream tools (GitHub
 * Teams, Teleport, CI/CD runners, automated tagging) key off it, so engineers
 * browsing the catalog had no way to read or copy it.
 */
export function EntityTeamIdentifierCard() {
  const classes = useStyles();
  const { entity } = useEntity();
  const machineId = entity.metadata.name;

  // `groupPage` renders for every `kind: Group` entity, not just teams, so the
  // copy is keyed off the catalog-normalized `team/level` annotation the same
  // way TeamActions (packages/app EntityPage) is.
  const levelLabel = groupLevelLabel(
    entity.metadata.annotations?.['team/level'],
  );

  return (
    <InfoCard title={`${levelLabel} Identifier`} variant="gridItem">
      <MachineIdDisplay
        machineId={machineId}
        showPermanenceWarning={false}
        levelLabel={levelLabel}
        endAdornment={
          <InlineCopyButton
            text={machineId}
            ariaLabel="Copy Machine ID"
            className={classes.copy}
            iconClassName={classes.copyIcon}
          />
        }
      />
      <Typography variant="caption" className={classes.hint}>
        Canonical identifier used by downstream tools (GitHub Teams, Teleport,
        CI/CD runners, automated tagging) to map services to this group.
      </Typography>
    </InfoCard>
  );
}
